import { useState } from 'react';
import { Box, Typography, List, ListItem, ListItemIcon, ListItemText, ListItemButton, Divider, Collapse } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useNavigate, useLocation } from 'react-router-dom';
import DashboardIcon from '@mui/icons-material/Dashboard';
import BusinessIcon from '@mui/icons-material/Business';
import GroupIcon from '@mui/icons-material/Group';
import PaymentIcon from '@mui/icons-material/Payment';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DescriptionIcon from '@mui/icons-material/Description';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BadgeIcon from '@mui/icons-material/Badge';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';

// Shared menu items config — same items as Sidebar.js
const getMenuItems = (userProfile) => {
  const generalSchema = userProfile?.ui_config?.orders_general_configs?.general_schema || {};
  const productPluralLabel = generalSchema.product?.plural_label || 'מוצרים';
  const orderPluralLabel = generalSchema.order?.plural_label || 'הזמנות';
  const newOrderLabel = generalSchema.new_order?.label || 'הזמנה חדשה';

  return [
    { page: 'DashboardPage', label: 'כללי', icon: <DashboardIcon />, path: '/dashboard' },
    { page: 'ClientsListPageCached', label: 'לקוחות', icon: <BusinessIcon />, path: '/clients' },
    { page: 'SuppliersPage', label: 'ספקים', icon: <LocalShippingIcon />, path: '/suppliers' },
    { page: 'TransactionsPage', label: 'דוח תנועות', icon: <PaymentIcon />, path: '/transactions' },
    { page: 'CashFlowPage', label: 'תזרים מזומנים', icon: <AccountBalanceIcon />, path: '/cashflow' },
    { page: 'EmployeesPage', label: 'עובדים', icon: <BadgeIcon />, path: '/employees' },
    { page: 'GroupsManagementPage', label: 'ניהול קבוצות', icon: <GroupIcon />, path: '/groups' },
    { page: 'FunctionalGroupsPage', label: 'פעילות קבוצתית', icon: <GroupWorkIcon />, path: '/functional-groups' },
    { page: 'YearlyFeeManagementPage', label: 'ניהול שכר טרחה', icon: <MonetizationOnIcon />, path: '/yearly-fee' },
    { page: 'ProductsPage', label: productPluralLabel, icon: <ShoppingCartIcon />, path: '/products', subItems: [
      { label: productPluralLabel, path: '/products' },
      { page: 'InventoryActionPage', label: 'פעולת מלאי', icon: <QrCodeScannerIcon />, path: '/inventory-action' },
    ]},
    { page: 'OrdersPage', label: orderPluralLabel, icon: <ReceiptIcon />, path: '/orders', subItems: [
      { label: newOrderLabel, path: '/order/new' },
      { label: orderPluralLabel, path: '/orders' },
      { label: 'הצעות מחיר', icon: <DescriptionIcon />, path: '/quotations' },
    ]},
    { page: 'DeletedClientsPage', label: 'לקוחות שנמחקו', icon: <DeleteSweepIcon />, path: '/clients/deleted' },
  ];
};

const MobileDrawerContent = ({ userProfile, onNavigate }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const role = userProfile?.role;
  const allowedPages = userProfile?.ui_config?.allowed_pages || [];

  const alwaysAllowedPages = [
    'DashboardPage', 'SuppliersPage', 'SupplierDetailPage',
    'ClientsListPageCached', 'ClientDetailPage',
    'ChangePasswordPage', 'UpdateProfilePage',
    'MinimalPrintPage', 'CashFlowPage',
  ];

  const hasPageAccess = (pageName) => {
    const isDefault = alwaysAllowedPages.includes(pageName);
    if (!isDefault && !allowedPages.includes(pageName)) return false;

    const defaultRoles = {
      'DashboardPage': ['manager', 'employee'],
      'ClientsListPageCached': ['manager', 'employee'],
      'DeletedClientsPage': ['manager'],
      'TransactionsPage': ['manager'],
      'FunctionalGroupsPage': ['manager'],
      'YearlyFeeManagementPage': ['manager'],
      'SuppliersPage': ['manager', 'employee'],
      'CashFlowPage': ['manager'],
      'EmployeesPage': ['manager'],
      'GroupsManagementPage': ['manager'],
    };

    if (!userProfile?.ui_config?.pages_configs) {
      return defaultRoles[pageName]?.includes(role) || false;
    }

    const pageConfig = userProfile.ui_config.pages_configs[pageName];
    if (pageConfig?.allowed_roles) {
      return pageConfig.allowed_roles.includes(role);
    }

    return defaultRoles[pageName]?.includes(role) || false;
  };

  const menuItems = getMenuItems(userProfile);
  const [expandedMenus, setExpandedMenus] = useState({});

  const handleClick = (path) => {
    navigate(path);
    onNavigate();
  };

  const toggleExpand = (label) => {
    setExpandedMenus(prev => ({ ...prev, [label]: !prev[label] }));
  };

  return (
    <Box sx={{ pt: 1.5 }}>
      {/* Header */}
      <Box sx={{ px: 2, pb: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <img
          src="/logo.png"
          alt="BalancePro"
          style={{ height: '28px', width: 'auto' }}
        />
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
          תפריט
        </Typography>
      </Box>

      <Divider />

      {/* Menu Items */}
      <List sx={{ px: 1, pt: 0.5 }}>
        {menuItems.map((item) => {
          if (!hasPageAccess(item.page)) return null;

          // Items with sub-items (e.g., Orders → Orders + Quotations)
          if (item.subItems) {
            const isAnySubActive = item.subItems.some(sub =>
              location.pathname === sub.path || location.pathname.startsWith(sub.path)
            );
            const isExpanded = expandedMenus[item.label] ?? isAnySubActive;

            return (
              <Box key={item.path}>
                <ListItem disablePadding sx={{ mb: 0.25 }}>
                  <ListItemButton
                    onClick={() => toggleExpand(item.label)}
                    sx={{
                      borderRadius: '8px',
                      pl: 2,
                      backgroundColor: isAnySubActive && !isExpanded ? 'primary.main' : 'transparent',
                      color: isAnySubActive && !isExpanded ? 'white' : 'text.primary',
                      '&:hover': { backgroundColor: 'action.hover' },
                    }}
                  >
                    <ListItemIcon sx={{
                      color: isAnySubActive ? 'primary.main' : 'text.secondary',
                      minWidth: 40,
                    }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ fontWeight: isAnySubActive ? 600 : 400, fontSize: '0.95rem' }}
                    />
                    {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  </ListItemButton>
                </ListItem>
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <List disablePadding>
                    {item.subItems.map((sub) => {
                      const isSubActive = location.pathname === sub.path;
                      return (
                        <ListItem key={sub.path} disablePadding sx={{ mb: 0.25 }}>
                          <ListItemButton
                            onClick={() => handleClick(sub.path)}
                            sx={{
                              borderRadius: '8px',
                              pl: 5,
                              backgroundColor: isSubActive ? 'primary.main' : 'transparent',
                              color: isSubActive ? 'white' : 'text.primary',
                              '&:hover': { backgroundColor: isSubActive ? 'primary.dark' : 'action.hover' },
                            }}
                          >
                            {sub.icon && (
                              <ListItemIcon sx={{ color: isSubActive ? 'white' : 'text.secondary', minWidth: 36 }}>
                                {sub.icon}
                              </ListItemIcon>
                            )}
                            <ListItemText
                              primary={sub.label}
                              primaryTypographyProps={{ fontWeight: isSubActive ? 600 : 400, fontSize: '0.9rem' }}
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Collapse>
              </Box>
            );
          }

          // Regular menu item
          const isActive = location.pathname === item.path ||
            (item.path !== '/dashboard' && location.pathname.startsWith(item.path));

          return (
            <ListItem key={item.path} disablePadding sx={{ mb: 0.25 }}>
              <ListItemButton
                onClick={() => handleClick(item.path)}
                sx={{
                  borderRadius: '8px',
                  pl: 2,
                  backgroundColor: isActive ? 'primary.main' : 'transparent',
                  color: isActive ? 'white' : 'text.primary',
                  '&:hover': {
                    backgroundColor: isActive ? 'primary.dark' : 'action.hover',
                  },
                }}
              >
                <ListItemIcon sx={{
                  color: isActive ? 'white' : 'text.secondary',
                  minWidth: 40,
                }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontWeight: isActive ? 600 : 400,
                    fontSize: '0.95rem',
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
};

export default MobileDrawerContent;
